import { useEffect, useState } from "react";
import { registerStoryblokBridge, useStoryblokApi } from "@storyblok/react";

// Like @storyblok/react's useStoryblok (same fetch, same visual-editor bridge
// for live preview), but it reports failures instead of leaving them as
// unhandled promise rejections, so a missing story can render a not-found
// page. Returns { status: "loading" | "found" | "not-found" | "error", story }.
export const useStory = (slug, params = {}) => {
  const storyblokApi = useStoryblokApi();
  const [result, setResult] = useState({ status: "loading", story: null });
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    if (!storyblokApi) {
      setResult({ status: "error", story: null });
      return;
    }

    // Ignore a response that arrives after the user has moved on.
    let current = true;
    setResult({ status: "loading", story: null });

    storyblokApi
      .get(`cdn/stories/${slug}`, JSON.parse(paramsKey))
      .then(({ data }) => {
        if (!current) return;
        setResult({ status: "found", story: data.story });

        // In Storyblok's visual editor, re-render on unsaved edits.
        const inEditor = typeof window.storyblokRegisterEvent !== "undefined";
        if (inEditor && data.story.id) {
          const parsed = JSON.parse(paramsKey);
          registerStoryblokBridge(
            data.story.id,
            (story) => setResult({ status: "found", story }),
            { resolveRelations: parsed.resolve_relations, resolveLinks: parsed.resolve_links },
          );
        }
      })
      .catch((error) => {
        if (!current) return;
        setResult({ status: error?.status === 404 ? "not-found" : "error", story: null });
      });

    return () => {
      current = false;
    };
  }, [storyblokApi, slug, paramsKey]);

  return result;
};
